<template>
  <div>
    <loading-component :value="loading" />

    <v-card>
      <div class="fin-header">
        <div class="fin-toolbar">
          <span class="fin-title">{{ $t("reports.catalogTitle") }}</span>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat color="primary" @click="load">
              <v-icon small>refresh</v-icon>
            </v-btn>
            <span>{{ $t("reports.refresh") }}</span>
          </v-tooltip>
        </div>
        <div class="fin-filters">
          <v-text-field
            v-model="search"
            :label="$t('reports.searchCatalog')"
            append-icon="search"
            hide-details
            clearable
            class="fin-field"
          ></v-text-field>
          <v-select
            v-model="statusFilter"
            :items="statusItems"
            item-text="text"
            item-value="value"
            :label="$t('reports.status')"
            hide-details
            class="fin-field"
          ></v-select>
        </div>
      </div>

      <v-divider></v-divider>

      <div v-if="!loading && !visibleFamilies.length" class="catalog__empty">
        {{ $t("reports.noReportsPermitted") }}
      </div>

      <div v-for="family in visibleFamilies" :key="family.key" class="catalog__family">
        <div class="catalog__family-title">
          {{ family.label }}
          <span class="catalog__family-count">({{ family.reports.length }})</span>
        </div>

        <v-layout row wrap>
          <v-flex
            v-for="report in family.reports"
            :key="report.slug"
            xs12
            sm6
            md4
            class="catalog__cell"
          >
            <v-card
              flat
              class="catalog__card"
              :class="{ 'catalog__card--blocked': report.status === 'BLOCKED' }"
              @click="open(report)"
            >
              <div class="catalog__card-head">
                <span class="catalog__card-title">{{ report.title_ar }}</span>
                <v-chip
                  small
                  disabled
                  :color="statusColor(report.status)"
                  text-color="white"
                  class="catalog__chip"
                >
                  {{ statusLabel(report.status) }}
                </v-chip>
              </div>
              <div class="catalog__card-body">{{ report.description_ar }}</div>
            </v-card>
          </v-flex>
        </v-layout>
      </div>
    </v-card>
  </div>
</template>

<script>
import LoadingComponent from "@/components/LoadingComponent";

/*
 * The report catalog (requirements doc 2026-09-09).
 *
 * This page is the complete, honest map of the document's fifty reports. It
 * deliberately lists three kinds of entry:
 *
 *   READY / PARTIAL  open in the generic report page
 *   LEGACY           already shipped with a purpose-built page; the card
 *                    routes there instead of re-rendering a tested report
 *   BLOCKED          the data does not exist. The card opens an explanation
 *                    of exactly what is missing, rather than being silently
 *                    dropped from the list -- an omitted report looks like an
 *                    oversight, a blocked one is an answer.
 *
 * The server already filters the list to what the caller may open, so a user
 * never sees a card that would 403.
 */
export default {
  name: "ReportCatalog",
  components: { LoadingComponent },
  data() {
    return {
      loading: false,
      reports: [],
      familyLabels: {},
      search: "",
      statusFilter: "ALL",
    };
  },
  computed: {
    statusItems() {
      return [
        { value: "ALL", text: this.$t("reports.statusAll") },
        { value: "READY", text: this.$t("reports.statusReady") },
        // PARTIAL is a status ten reports actually carry and the cards already
        // render; leaving it out of the picker made those ten unfilterable.
        { value: "PARTIAL", text: this.$t("reports.statusPartial") },
        { value: "LEGACY", text: this.$t("reports.statusLegacy") },
        { value: "BLOCKED", text: this.$t("reports.statusBlocked") },
      ];
    },
    filteredReports() {
      // Lower-cased on both sides: the English titles and the slugs are half
      // of what is searched here, and a case-sensitive match made "captain"
      // find nothing while "Captain" found eleven reports.
      const term = (this.search || "").trim().toLowerCase();
      return this.reports.filter((report) => {
        if (this.statusFilter !== "ALL" && report.status !== this.statusFilter) {
          return false;
        }
        if (!term) {
          return true;
        }
        const haystack = [report.title_ar, report.title_en, report.description_ar, report.slug]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.indexOf(term) !== -1;
      });
    },
    /** Grouped by the document's own section numbering. */
    visibleFamilies() {
      const groups = {};
      this.filteredReports.forEach((report) => {
        if (!groups[report.family]) {
          groups[report.family] = [];
        }
        groups[report.family].push(report);
      });
      return Object.keys(groups)
        .sort()
        .map((key) => ({
          key: key,
          label: this.familyLabels[key] || key,
          reports: groups[key],
        }));
    },
  },
  created() {
    this.load();
  },
  methods: {
    /*
     * Every finance page in this dashboard carries its own copy: the helper in
     * main.js is module-private, so calling it as a method threw a TypeError
     * inside the catch handler and a failed catalog load reached the operator
     * as an empty screen with no toast at all.
     */
    getErrorMessage(error, fallback) {
      if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }

      return fallback;
    },
    load() {
      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.REPORTS)
        .then(
          function (response) {
            const data = response.data.data || {};
            this.reports = data.reports || [];
            this.familyLabels = data.families || {};
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("reports.failedToLoad")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    statusColor(status) {
      if (status === "BLOCKED") {
        return "grey";
      }
      if (status === "LEGACY") {
        return "blue-grey";
      }
      if (status === "PARTIAL") {
        return "orange";
      }
      return "green";
    },
    statusLabel(status) {
      return this.$t("reports.status" + status.charAt(0) + status.slice(1).toLowerCase());
    },
    /**
     * Whether this dashboard actually has a route by that name. The name is
     * server-supplied, and vue-router does not fail loudly on an unknown one:
     * it resolves to "/" and dumps the reader on the requests screen, which
     * reads as the card being dead.
     */
    hasRoute(name) {
      const walk = (routes) =>
        (routes || []).some((route) => route.name === name || walk(route.children));

      return walk(this.$router.options.routes);
    },
    open(report) {
      // A legacy report keeps its own purpose-built page: routing there is
      // the whole point of registering it, not re-rendering it generically.
      // When that page does not exist in this dashboard (a captain's shift log
      // lives inside the captain profile and has no route of its own), the
      // generic page is the fallback -- it renders the report's own note
      // saying where the figures are published.
      if (report.legacy_view && this.hasRoute(report.legacy_view)) {
        this.$router.push({ name: report.legacy_view });
        return;
      }
      this.$router.push({ name: "ReportPage", params: { slug: report.slug } });
    },
  },
};
</script>

<!-- Shared compact finance styling. Imported directly because this page renders
     no <finance-section-nav />, which is what carries it on the finance pages:
     without it the fin-* classes here (the header, the toolbar, the filter row)
     would resolve to nothing. -->
<style src="@/assets/finance-compact.css"></style>

<style scoped>
.catalog__family {
  padding: 8px 12px 4px;
}
.catalog__family-title {
  font-size: 15px;
  font-weight: 700;
  padding: 8px 4px;
}
.catalog__family-count {
  font-weight: 400;
  opacity: 0.6;
  font-size: 13px;
}
.catalog__cell {
  padding: 6px;
}
.catalog__card {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  padding: 10px 12px;
  height: 100%;
  cursor: pointer;
}
.catalog__card:hover {
  border-color: rgba(0, 0, 0, 0.32);
}
.catalog__card--blocked {
  background: #fafafa;
}
.catalog__card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}
.catalog__card-title {
  font-size: 14px;
  font-weight: 700;
  line-height: 1.4;
}
.catalog__chip {
  flex: 0 0 auto;
  margin: 0;
}
.catalog__card-body {
  font-size: 12px;
  opacity: 0.75;
  margin-top: 6px;
  line-height: 1.6;
}
.catalog__empty {
  padding: 32px;
  text-align: center;
  opacity: 0.6;
}
</style>
